import React, { useState, useMemo } from 'react';
import JSZip from 'jszip';
import './App.css';

interface Config {
  systems: {
    [systemName: string]: {
      [deviceName: string]: boolean;
    };
  };
}

const defaultConfig: Config = {
  systems: {
    Lavva: {
      "Lavva LED": true,
      "Lavve Gate": false,
      "Wiśniowski Gate": false,
      "SmartAWSC": true,
    },
    Exalus: {
      "Lavva LED": false,
      "Lavve Gate": true,
      "Wiśniowski Gate": false,
      "SmartAWSC": false,
    },
    Wiśniowski: {
      "Lavva LED": false,
      "Lavve Gate": false,
      "Wiśniowski Gate": true,
      "SmartAWSC": false,
    },
  },
};

const App: React.FC = () => {
  const [config, setConfig] = useState<Config>(defaultConfig);
  const [newSystem, setNewSystem] = useState<string>('');
  const [newDevice, setNewDevice] = useState<string>('');

  // Calculate the union of all device names across systems
  const deviceNames = useMemo(() => {
    const devicesSet = new Set<string>();
    Object.values(config.systems).forEach(system => {
      Object.keys(system).forEach(device => devicesSet.add(device));
    });
    return Array.from(devicesSet);
  }, [config.systems]);

  // Toggle the checkbox for a given system and device
  const toggleCompatibility = (systemName: string, device: string) => {
    setConfig(prev => {
      const system = prev.systems[systemName];
      const current = system[device] || false;
      return {
        systems: {
          ...prev.systems,
          [systemName]: { ...system, [device]: !current },
        },
      };
    });
  };

  // Add a new system; initialize with all known devices set to false
  const addSystem = () => {
    if (newSystem.trim() === '') return;
    if (config.systems[newSystem]) {
      alert('System already exists!');
      return;
    }
    const newSystemData: { [device: string]: boolean } = {};
    deviceNames.forEach(device => {
      newSystemData[device] = false;
    });
    setConfig(prev => ({
      systems: {
        ...prev.systems,
        [newSystem]: newSystemData,
      },
    }));
    setNewSystem('');
  };

  // Add a new device to every system (if it does not already exist), defaulting to false
  const addDevice = () => {
    if (newDevice.trim() === '') return;
    setConfig(prev => {
      const updatedSystems: { [systemName: string]: { [device: string]: boolean } } = {};
      Object.entries(prev.systems).forEach(([systemName, system]) => {
        updatedSystems[systemName] = {
          ...system,
          [newDevice]: system.hasOwnProperty(newDevice) ? system[newDevice] : false,
        };
      });
      return { systems: updatedSystems };
    });
    setNewDevice('');
  };

  // Remove a system by name
  const removeSystem = (systemName: string) => {
    if (window.confirm(`Are you sure you want to remove system "${systemName}"?`)) {
      setConfig(prev => {
        const newSystems = { ...prev.systems };
        delete newSystems[systemName];
        return { systems: newSystems };
      });
    }
  };

  // Remove a device from all systems
  const removeDevice = (device: string) => {
    if (window.confirm(`Are you sure you want to remove device "${device}"?`)) {
      setConfig(prev => {
        const updatedSystems: { [systemName: string]: { [device: string]: boolean } } = {};
        Object.entries(prev.systems).forEach(([systemName, system]) => {
          const newSystem = { ...system };
          if (newSystem.hasOwnProperty(device)) {
            delete newSystem[device];
          }
          updatedSystems[systemName] = newSystem;
        });
        return { systems: updatedSystems };
      });
    }
  };

  // Check all devices for a given system (set all to true)
  const checkAllForSystem = (systemName: string) => {
    setConfig(prev => {
      const system = prev.systems[systemName];
      const newSystem = Object.fromEntries(
        Object.entries(system).map(([device, val]) => [device, true])
      );
      return {
        systems: {
          ...prev.systems,
          [systemName]: newSystem,
        },
      };
    });
  };

  // Export configuration as a ZIP file with a separate JSON file per system
  const exportConfig = async () => {
    const zip = new JSZip();
    Object.entries(config.systems).forEach(([systemName, compatibility]) => {
      zip.file(`${systemName}.json`, JSON.stringify(compatibility, null, 2));
    });
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'config.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import configuration from a ZIP file containing JSON files for each system
  const importConfig = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const zip = new JSZip();
    try {
      const zipContent = await zip.loadAsync(file);
      const systemFiles = Object.keys(zipContent.files)
        .filter(filename => filename.endsWith('.json'));
      const systemsArray = await Promise.all(systemFiles.map(async (filename) => {
        const fileData = zipContent.file(filename);
        if (fileData) {
          const text = await fileData.async('string');
          const compatibility = JSON.parse(text) as { [deviceName: string]: boolean };
          const systemName = filename.replace(/\.json$/, '');
          return { systemName, compatibility };
        }
        return null;
      }));
      const systems = systemsArray.reduce((acc, item) => {
        if (item) {
          acc[item.systemName] = item.compatibility;
        }
        return acc;
      }, {} as { [systemName: string]: { [deviceName: string]: boolean } });
      setConfig({ systems });
    } catch (error) {
      console.error("Error importing file:", error);
      alert("Error importing file.");
    }
  };

  return (
    <div className="container">
      <h1 className="header">Device Compatibility Map</h1>

      <div className="section">
        <h2>Add New System</h2>
        <input
          className="input"
          type="text"
          value={newSystem}
          onChange={(e) => setNewSystem(e.target.value)}
          placeholder="System name"
        />
        <button className="button" onClick={addSystem}>Add System</button>
      </div>

      <div className="section">
        <h2>Add New Device</h2>
        <input
          className="input"
          type="text"
          value={newDevice}
          onChange={(e) => setNewDevice(e.target.value)}
          placeholder="Device name"
        />
        <button className="button" onClick={addDevice}>Add Device</button>
      </div>

      <div className="section table-container">
        <h2>Configuration Table</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Device / System</th>
              {Object.keys(config.systems).map(systemName => (
                <th key={systemName}>
                  <div className="header-cell">
                    <span>{systemName}</span>
                    <div className="header-cell-buttons">
                      <button
                        className="check-all-button"
                        onClick={() => checkAllForSystem(systemName)}
                      >
                        Check All
                      </button>
                      <button
                        className="remove-button"
                        onClick={() => removeSystem(systemName)}
                      >
                        x
                      </button>
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deviceNames.map(device => (
              <tr key={device}>
                <td>
                  <div className="row-cell">
                    <span>{device}</span>
                    <button
                      className="remove-button"
                      onClick={() => removeDevice(device)}
                    >
                      x
                    </button>
                  </div>
                </td>
                {Object.entries(config.systems).map(([systemName, compatibility]) => (
                  <td key={systemName}>
                    <input
                      type="checkbox"
                      checked={compatibility[device] || false}
                      onChange={() => toggleCompatibility(systemName, device)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section">
        <button className="button" onClick={exportConfig}>
          Export Configuration (ZIP)
        </button>
        <input
          type="file"
          accept=".zip"
          onChange={importConfig}
          style={{ marginLeft: '10px' }}
        />
      </div>
    </div>
  );
};

export default App;
